import { Device } from "@fairfleet/geotab";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import Box from "@mui/material/Box";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { AUDIT_DEVICE_FIELDS } from "../../hooks/useAuditDevices";
import { AuditFilters } from "../../hooks/useAuditFilters";
import { ToolbarIconButton } from "../common/ToolbarIconButton";
import { ReasonMultiSelect } from "./ReasonMultiSelect";
import { DateRangeSelect } from "@/components/input/DateRangeSelect";
import { DriverOrDeviceSelect } from "@/components/input/DriverOrDeviceSelect";

interface Props {
  filters: AuditFilters;
  includeArchived: boolean;
  /** Rendered at the end of the row, e.g. the summary toggle. */
  trailing?: ReactNode;
}

/** Vehicles, reason and period in one row, then the reset button and `trailing`. The group filter sits in the toolbar. */
export function FilterBar({ filters, includeArchived, trailing }: Props) {
  const { t } = useTranslation();

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1.4fr 1.1fr 1fr auto" },
        gap: "8px",
        alignItems: "center",
        mb: "8px",
      }}
    >
      <DriverOrDeviceSelect
        devices={filters.devices}
        onChange={(_: unknown, devices?: Device[]) => filters.setDevices(devices ?? [])}
        onlyDrivers={false}
        showArchived={false}
        showArchivedVehicles={includeArchived}
        showUsers={false}
        deviceFields={AUDIT_DEVICE_FIELDS}
        label={t("logbookAudit.filters.vehicle")}
        placeholder={t("logbookAudit.filters.vehiclePlaceholder")}
      />
      <ReasonMultiSelect value={filters.reasons} onChange={filters.setReasons} />
      <DateRangeSelect fromDate={filters.fromDay} toDate={filters.toDay} onChange={filters.setDateRange} />
      <Box sx={{ display: "flex", alignItems: "center", gap: "8px", gridColumn: { xs: "1 / -1", md: "auto" } }}>
        <ToolbarIconButton
          label={t("logbookAudit.filters.reset")}
          icon={<FilterAltOffIcon fontSize="small" aria-hidden />}
          onClick={filters.reset}
          disabled={!filters.isDirty}
        />
        {trailing}
      </Box>
    </Box>
  );
}
